export interface RouteConfig {
  path: string
  component: string
  title?: string
}

export interface RouteMatch {
  route: RouteConfig
  params: Record<string, string>
}

export class Router {
  private routes: RouteConfig[] = []
  private listeners: Array<(route: RouteMatch | null) => void> = []
  private currentRoute: RouteMatch | null = null

  constructor() {
    // Listen for browser navigation events
    window.addEventListener('popstate', (): void => {
      this.handleRouteChange()
    })
  }

  addRoute(config: RouteConfig): void {
    this.routes.push(config)
  }

  addRoutes(configs: RouteConfig[]): void {
    configs.forEach(config => this.addRoute(config))
  }

  navigate(path: string, options: { replace?: boolean } = {}): void {
    if (options.replace) {
      // Overwrite the current history entry instead of adding a new one. Used
      // for in-page tab switches so the back button returns to the previous
      // page rather than walking back through tabs.
      window.history.replaceState(null, '', path)
    } else {
      window.history.pushState(null, '', path)
    }
    this.handleRouteChange()
  }

  back(): void {
    window.history.back()
  }

  subscribe(listener: (route: RouteMatch | null) => void): () => void {
    this.listeners.push(listener)
    // Immediately call with current route
    listener(this.currentRoute)

    return (): void => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  getCurrentRoute(): RouteMatch | null {
    return this.currentRoute
  }

  private handleRouteChange(): void {
    const path = window.location.pathname
    const match = this.matchRoute(path)
    this.currentRoute = match

    // Update document title if route has one
    if (match?.route.title) {
      document.title = `${match.route.title} - Cairn`
    }

    // Notify listeners
    this.listeners.forEach(listener => listener(match))
  }

  private matchRoute(path: string): RouteMatch | null {
    for (const route of this.routes) {
      const match = this.matchPath(route.path, path)
      if (match) {
        return { route, params: match }
      }
    }
    return null
  }

  private matchPath(routePath: string, currentPath: string): Record<string, string> | null {
    // Simple path matching with parameters
    const routeSegments = routePath.split('/').filter(Boolean)
    const pathSegments = currentPath.split('/').filter(Boolean)

    if (routeSegments.length !== pathSegments.length) {
      return null
    }

    const params: Record<string, string> = {}

    for (let i = 0; i < routeSegments.length; i++) {
      const routeSegment = routeSegments[i]
      const pathSegment = pathSegments[i]

      if (routeSegment.startsWith(':')) {
        // Parameter segment
        params[routeSegment.slice(1)] = pathSegment
      } else if (routeSegment !== pathSegment) {
        // Literal segment doesn't match
        return null
      }
    }

    return params
  }

  start(): void {
    // Initialize with current path
    this.handleRouteChange()
  }
}

// Create a singleton router instance
export const router = new Router()
